import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Maximize2, ArrowRight, ArrowUpRight, Plus } from "lucide-react";
import { EPILATION_EXTRAS, VAJACIAL, PLANITY_URL } from "@/data/content";
import { Reveal } from "./Reveal";
import { Lightbox } from "./Lightbox";
import { ASavoir } from "./ASavoir";
import { Dot } from "./DotText";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export const EpilationExtras = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const hasCard = !!EPILATION_EXTRAS.cardImage;
  const hasExtra = !!EPILATION_EXTRAS.extraImage;
  const carteImages = [EPILATION_EXTRAS.cardImage, EPILATION_EXTRAS.extraImage].filter(
    Boolean
  );

  const CartePage = ({ src, label, testid, imgIndex }) => (
    <button
      type="button"
      data-testid={testid}
      onClick={() => setLightboxIndex(imgIndex)}
      className="group relative block w-full overflow-hidden rounded-md border border-[#ece4d7]"
    >
      <img
        src={src}
        alt={`${label} — Ninaa Beauty`}
        loading="lazy"
        className="w-full h-auto object-contain transition-transform duration-[900ms] ease-out group-hover:scale-[1.02]"
      />
      <span className="absolute inset-0 bg-[#2b2a27]/0 group-hover:bg-[#2b2a27]/10 transition-colors duration-500" />
      <span className="absolute bottom-3 right-3 grid place-items-center h-9 w-9 rounded-full bg-white/90 text-[#4b2018]">
        <Maximize2 size={16} />
      </span>
    </button>
  );

  const Placeholder = ({ testid }) => (
    <div
      data-testid={testid}
      className="w-full aspect-[3/4] rounded-md border border-[#ece4d7] bg-[#f6f1e9] grid place-items-center text-[#b07a63]"
    >
      <div className="flex flex-col items-center">
        <Camera size={26} strokeWidth={1.2} />
        <span className="mt-3 text-[11px] tracking-[0.22em] uppercase">
          Aperçu à venir
        </span>
      </div>
    </div>
  );

  return (
    <div className="mt-20 md:mt-28 space-y-20 md:space-y-24">
      {/* Vajacial — titre bien visible + carte photo cliquable */}
      <Reveal>
        <div className="flex flex-col max-w-4xl">
          <h2 className="font-serif-display font-light text-[#4b2018] text-3xl md:text-4xl mb-2">
            Le Vajacial, qu’est-ce que c’est&nbsp;?
          </h2>
          <p className="text-sm text-[#4b2018] mb-6">{VAJACIAL.subtitle}</p>
          <Link
            to="/vajacial"
            data-testid="vajacial-card"
            aria-label="Découvrir le Vajacial"
            className="group block w-full max-w-sm bg-white rounded-md overflow-hidden border border-[#ece4d7] hover:shadow-[0_20px_40px_-24px_rgba(43,42,39,0.35)] transition-shadow duration-500"
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={VAJACIAL.image}
                alt="Le Vajacial — Ninaa Beauty"
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-[#2b2a27]/35 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-500">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/10 backdrop-blur-sm px-5 py-2.5 text-sm tracking-wide text-white md:translate-y-1 md:group-hover:translate-y-0 transition-transform duration-500">
                  Découvrir <ArrowUpRight size={15} />
                </span>
              </div>
            </div>
          </Link>
        </div>
      </Reveal>

      {/* La carte des épilations (2 pages) */}
      <Reveal className="max-w-3xl">
        <h2 className="font-serif-display font-light text-[#4b2018] text-2xl md:text-3xl mb-2">
          La carte des épilations
        </h2>
        <p className="text-sm text-[#4b2018] mb-6">
          Cliquez sur une page pour consulter les prestations, forfaits et tarifs.
        </p>
        <div className="grid grid-cols-2 gap-5 md:gap-8 max-w-lg">
          {hasCard ? (
            <CartePage
              src={EPILATION_EXTRAS.cardImage}
              label="Carte des épilations"
              testid="epilation-card-thumb"
              imgIndex={0}
            />
          ) : (
            <Placeholder testid="epilation-card-placeholder" />
          )}
          {hasExtra ? (
            <CartePage
              src={EPILATION_EXTRAS.extraImage}
              label="Forfaits épilations"
              testid="epilation-extra-photo"
              imgIndex={hasCard ? 1 : 0}
            />
          ) : (
            <Placeholder testid="epilation-extra-placeholder" />
          )}
        </div>
      </Reveal>

      {/* Modelage des jambes — encart placé sous la carte tarifaire */}
      <Reveal>
        <div className="flex flex-col">
          <p className="text-xs tracking-[0.3em] uppercase text-[#b07a63] mb-4">
            Prolongez votre moment
          </p>
          <a
            href={PLANITY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="epilation-modelage"
            className="group flex w-full max-w-sm items-start gap-4 rounded-md border border-[#ece4d7] bg-[#faf7f2] px-5 py-4 transition-colors duration-400 hover:border-[#d8c9b3] hover:bg-[#f3ece1]"
          >
            <span className="mt-0.5 grid place-items-center h-8 w-8 rounded-full bg-white text-[#b07a63] shrink-0 transition-transform duration-400 group-hover:scale-105">
              <Plus size={16} />
            </span>
            <div>
              <p className="text-sm text-[#4b2018]">
                <span className="font-medium">{EPILATION_EXTRAS.modelage.title}</span>
                <Dot />
                <span>{EPILATION_EXTRAS.modelage.meta}</span>
              </p>
              <p className="mt-1 text-sm leading-relaxed text-[#4b2018]">
                {EPILATION_EXTRAS.modelage.text}
              </p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-xs tracking-wide text-[#b07a63] transition-colors group-hover:text-[#4b2018]">
                Réserver ce modelage
                <ArrowRight
                  size={13}
                  className="transition-transform duration-300 group-hover:translate-x-0.5"
                />
              </span>
            </div>
          </a>
        </div>
      </Reveal>

      {/* Quelques précisions — accordéon discret */}
      <Reveal className="max-w-md">
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem
            value="precisions"
            data-testid="quelques-precisions-accordion"
            className="border border-[#ece4d7] rounded-md px-5"
          >
            <AccordionTrigger className="py-4 text-sm tracking-wide text-[#4b2018] hover:no-underline">
              Quelques précisions
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 pb-2">
                {EPILATION_EXTRAS.prep.slice(3).map((item, i) => (
                  <div key={i} data-testid={`epilation-precision-${i}`}>
                    <p className="text-xs tracking-[0.15em] uppercase text-[#b07a63] mb-1">
                      {item.t}
                    </p>
                    <p className="text-sm leading-relaxed text-[#4b2018] whitespace-pre-line">
                      {item.d}
                    </p>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Reveal>

      {/* À savoir → défile vers la section générale Préparer votre rendez-vous */}
      <Reveal>
        <ASavoir />
      </Reveal>

      <Lightbox
        open={lightboxIndex !== null}
        images={carteImages}
        index={lightboxIndex ?? 0}
        onIndexChange={setLightboxIndex}
        alt="Carte des épilations Ninaa Beauty"
        onClose={() => setLightboxIndex(null)}
      />
    </div>
  );
};
